import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Center, Spinner } from '@chakra-ui/react';
import { presentationAuth } from '../firebase/config';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  
  // Check if this is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // For admin routes, check Firebase auth directly instead of using the context
  if (isAdminRoute) {
    // Use the presentationAuth from config.ts, not firebase.ts
    const currentUser = presentationAuth.currentUser;
    
    // If we're on an admin route and not at the login page, check if user is authenticated
    if (!location.pathname.includes('/admin/login')) {
      if (!currentUser) {
        console.log('[ADMIN AUTH] Not authenticated for admin, redirecting to admin login');
        return <Navigate to="/admin/login" state={{ from: location }} replace />;
      }
      
      console.log('[ADMIN AUTH] User authenticated for admin, rendering admin content');
      return <>{children}</>;
    }
  }

  // For non-admin routes, use the regular auth flow
  if (isLoading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="methodist.navy" thickness="4px" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    console.log('[AUTH] Not authenticated, redirecting to login from:', location.pathname);
    // Redirect to login while saving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log('[AUTH] Authenticated, rendering protected content');
  return <>{children}</>;
}
